import { useTranslation } from "@next-platform/client-common-core";
import RulebookPickerTab from "@next-platform/client-common-core/rulebook/picker/RulebookPickerTab";
import { ButtonVariant } from "@next-platform/client-common-spicy-ui";
import type { RulebookSummary } from "@next-platform/packages-ts-types";
import type React from "react";
import { useCallback, useMemo } from "react";

type RulebookPick = {
  id: string | null;
  rulebook?: RulebookSummary;
};

type UseModalRulebookActionsArgs = {
  tab: RulebookPickerTab;
  pick: RulebookPick;
  isUnchanged: boolean;
  rulebooks: RulebookSummary[] | null;
  isLoading: boolean;
  hasError: boolean;
  isDiscoverTab: boolean;
  hasChoices: boolean;
  selectedRulebookId: string | null;
  onRulebookSelected: (rulebookId: string, rulebook?: RulebookSummary) => void;
  onClose: () => void;
};

/** A Mine-tab pick is stale once a refreshed list no longer contains it (Discover picks come from the index). */
const isMinePickMissing = ({ tab, rulebooks, pickId }: { tab: RulebookPickerTab; rulebooks: RulebookSummary[] | null; pickId: string | null }) =>
  tab === RulebookPickerTab.Mine && rulebooks !== null && Boolean(pickId) && !rulebooks.some((rulebook) => rulebook.id === pickId);

const resolveIsConfirmDisabled = ({
  tab,
  pick,
  isUnchanged,
  rulebooks,
  isLoading,
  hasError,
  isDiscoverTab,
}: Omit<UseModalRulebookActionsArgs, "hasChoices" | "selectedRulebookId" | "onRulebookSelected" | "onClose">) => {
  if (!pick.id || isUnchanged || (hasError && !isDiscoverTab)) {
    return true;
  }
  return tab === RulebookPickerTab.Mine && (isLoading || isMinePickMissing({ tab, rulebooks, pickId: pick.id }));
};

/** Cancel / Attach (or Replace) footer of the Rulebook picker; confirm stays off until a different, still-listed Rulebook is picked. */
const useModalRulebookActions = ({
  tab,
  pick,
  isUnchanged,
  rulebooks,
  isLoading,
  hasError,
  isDiscoverTab,
  hasChoices,
  selectedRulebookId,
  onRulebookSelected,
  onClose,
}: UseModalRulebookActionsArgs): React.ReactNode => {
  const { t } = useTranslation();

  const handleSubmit = useCallback(() => {
    if (pick.id && !isUnchanged) {
      onRulebookSelected(pick.id, pick.rulebook);
      onClose();
    }
  }, [pick, isUnchanged, onRulebookSelected, onClose]);

  const confirmLabel = selectedRulebookId
    ? t("rulebookEditor:attach.button.replace", "Replace Rulebook")
    : t("rulebookEditor:attach.button.attach", "Attach Rulebook");
  const isConfirmDisabled = useMemo(
    () => resolveIsConfirmDisabled({ tab, pick, isUnchanged, rulebooks, isLoading, hasError, isDiscoverTab }),
    [tab, pick, isUnchanged, rulebooks, isLoading, hasError, isDiscoverTab],
  );

  return useMemo(
    () =>
      hasChoices
        ? ([
            { label: t("common:cancel"), variant: ButtonVariant.TEXT, type: "button", onClick: onClose },
            { label: confirmLabel, variant: ButtonVariant.CONTAINED, type: "submit", disabled: isConfirmDisabled, onClick: handleSubmit },
          ] as React.ReactNode)
        : null,
    [hasChoices, t, onClose, confirmLabel, isConfirmDisabled, handleSubmit],
  );
};

export default useModalRulebookActions;
